import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sensitive } from "@/components/security/Sensitive";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Check, Archive, TrendingUp, Clock, Wallet, Repeat } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const emptyForm = () => ({
    date: new Date().toISOString().slice(0, 10),
    source: "",
    category: "Salary",
    amount: 0,
    currency: "INR",
    bank_account_id: "",
    recurring: false,
    frequency: "Monthly",
    next_expected_date: "",
    tds: 0,
    gross_amount: "",
    business_personal: "Personal",
    received: true,
    expected_date: "",
    invoice_number: "",
    invoice_date: "",
    client: "",
    project: "",
    follow_up_date: "",
    remarks: "",
});

export default function Income() {
    const { hasRole } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [meta, setMeta] = useState({ categories: [], frequencies: [] });
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [banks, setBanks] = useState([]);
    const [tab, setTab] = useState("received");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [busy, setBusy] = useState(false);

    const load = () => {
        api.get("/income").then((r) => setRows(r.data || []));
        api.get("/income/summary").then((r) => setSummary(r.data));
        api.get("/income/meta").then((r) => setMeta(r.data));
        api.get("/bank-accounts").then((r) => setBanks(r.data || [])).catch(() => setBanks([]));
    };
    useEffect(() => { load(); }, []);

    // Deep-link support — /income?add=1&amount=X&source=Y auto-opens the
    // Log-Income dialog with the amount + source prefilled. Used by the
    // Home QuickAdd FAB and the ⌘K palette smart-hit for "log income 500".
    useEffect(() => {
        if (searchParams.get("add") !== "1" || open) return;
        setEditing(null);
        setForm({
            ...emptyForm(),
            amount: searchParams.get("amount") || 0,
            source: searchParams.get("source") || "",
        });
        setOpen(true);
        // Strip the deep-link params so a page refresh doesn't re-trigger it
        const next = new URLSearchParams(searchParams);
        next.delete("add");
        next.delete("amount");
        next.delete("source");
        setSearchParams(next, { replace: true });
    }, [searchParams, open, setSearchParams]);

    const filtered = useMemo(() => {
        if (tab === "received") return rows.filter((r) => r.received);
        if (tab === "pending") return rows.filter((r) => !r.received);
        return rows;
    }, [rows, tab]);

    const openNew = () => {
        setEditing(null);
        setForm(emptyForm());
        setOpen(true);
    };
    const openEdit = (row) => {
        setEditing(row);
        setForm({ ...emptyForm(), ...row });
        setOpen(true);
    };

    const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
    const setInput = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e?.preventDefault();
        setBusy(true);
        try {
            const payload = { ...form };
            ["amount", "tds"].forEach((k) => (payload[k] = Number(payload[k] || 0)));
            payload.gross_amount = payload.gross_amount === "" ? null : Number(payload.gross_amount);
            if (!payload.recurring) {
                delete payload.frequency;
                delete payload.next_expected_date;
            }
            if (editing) {
                await api.patch(`/income/${editing.id}`, payload);
                toast.success("Income updated");
            } else {
                await api.post("/income", payload);
                toast.success("Income added");
            }
            setOpen(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to save");
        } finally {
            setBusy(false);
        }
    };

    const markReceived = async (id) => {
        try {
            await api.post(`/income/${id}/mark-received`);
            toast.success("Marked received");
            load();
        } catch (e) {
            toast.error("Failed to mark received");
        }
    };
    const archive = async (id) => {
        if (!window.confirm("Archive this income entry?")) return;
        try {
            await api.delete(`/income/${id}`);
            toast.success("Archived");
            load();
        } catch (e) {
            toast.error("Failed to archive");
        }
    };

    return (
        <div data-testid="income-page">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="eyebrow">Income & Receivables · Money in</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        All the money coming in.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3">
                        Salary, business, freelance, refunds, receivables — everything on the inflow side.
                    </p>
                </div>
                {hasRole("admin", "finance") && (
                    <Button onClick={openNew} data-testid="income-add-btn" className="bg-stone-900 hover:bg-stone-800 text-white">
                        <Plus size={16} className="mr-1.5" /> Log income
                    </Button>
                )}
            </div>

            {/* Summary KPIs */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="kpi-card">
                        <div className="eyebrow flex items-center gap-1.5"><TrendingUp size={11} /> This month received</div>
                        <div className="font-display text-2xl mt-2 tabular-nums">{fmtMoney(summary.this_month_received)}</div>
                    </div>
                    <div className="kpi-card">
                        <div className="eyebrow flex items-center gap-1.5"><Clock size={11} /> Pending receivables</div>
                        <div className="font-display text-2xl mt-2 tabular-nums text-amber-700">{fmtMoney(summary.pending_receivables)}</div>
                    </div>
                    <div className="kpi-card">
                        <div className="eyebrow flex items-center gap-1.5"><Repeat size={11} /> Recurring / month</div>
                        <div className="font-display text-2xl mt-2 tabular-nums">{fmtMoney(summary.recurring_monthly_estimate)}</div>
                    </div>
                    <div className="kpi-card">
                        <div className="eyebrow flex items-center gap-1.5"><Wallet size={11} /> YTD received</div>
                        <div className="font-display text-2xl mt-2 tabular-nums">{fmtMoney(summary.ytd_received)}</div>
                    </div>
                </div>
            )}

            <Tabs value={tab} onValueChange={setTab} className="mb-4">
                <TabsList data-testid="income-tabs">
                    <TabsTrigger value="received">Received</TabsTrigger>
                    <TabsTrigger value="pending">Pending · {summary?.pending_list?.length ?? 0}</TabsTrigger>
                    <TabsTrigger value="all">All</TabsTrigger>
                </TabsList>
                <TabsContent value={tab}>
                    <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                        <table className="dense-table w-full">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Source</th>
                                    <th>Category</th>
                                    <th>Client / Project</th>
                                    <th className="text-right">Amount</th>
                                    <th className="text-right">TDS</th>
                                    <th>Status</th>
                                    <th className="text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 && (
                                    <tr><td colSpan={8} className="text-center text-stone-500 py-14">No income entries yet in this view.</td></tr>
                                )}
                                {filtered.map((r) => (
                                    <tr key={r.id} data-testid={`income-row-${r.id}`}>
                                        <td className="tabular-nums">{fmtDate(r.date)}</td>
                                        <td className="font-medium text-stone-900">{r.source}</td>
                                        <td>
                                            <span className="text-xs px-2 py-0.5 rounded border border-stone-200 bg-stone-50 text-stone-700">{r.category}</span>
                                            {r.recurring && <span className="ml-1.5 text-[10px] text-blue-700 uppercase tracking-widest">· {r.frequency}</span>}
                                        </td>
                                        <td className="text-xs text-stone-600">{[r.client, r.project].filter(Boolean).join(" · ") || "—"}</td>
                                        <td className="text-right tabular-nums font-medium"><Sensitive level="highly_sensitive" as="span">{fmtMoney(r.amount)}</Sensitive></td>
                                        <td className="text-right tabular-nums text-stone-500">{r.tds ? fmtMoney(r.tds) : "—"}</td>
                                        <td>
                                            {r.received ? (
                                                <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><Check size={11} /> Received</span>
                                            ) : (
                                                <span className="text-amber-700 text-xs">Pending{r.expected_date ? ` · ${fmtDate(r.expected_date)}` : ""}</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="flex items-center justify-end gap-1">
                                                {!r.received && hasRole("admin", "finance") && (
                                                    <button
                                                        onClick={() => markReceived(r.id)}
                                                        title="Mark received"
                                                        className="p-1.5 rounded hover:bg-emerald-50 text-stone-500 hover:text-emerald-700"
                                                        data-testid={`income-received-${r.id}`}
                                                    >
                                                        <Check size={13} />
                                                    </button>
                                                )}
                                                {hasRole("admin", "finance") && (
                                                    <>
                                                        <button onClick={() => openEdit(r)} className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-900 text-xs">Edit</button>
                                                        <button onClick={() => archive(r.id)} className="p-1.5 rounded hover:bg-red-50 text-stone-500 hover:text-red-600">
                                                            <Archive size={13} />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>
            </Tabs>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    data-testid="income-form-dialog"
                    className="max-w-3xl h-[100dvh] sm:h-auto max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto rounded-none sm:rounded-lg p-5 sm:p-6"
                >
                    <DialogHeader>
                        <DialogTitle className="text-2xl sm:text-lg">{editing ? "Edit income" : "Log income"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label className="eyebrow">Date</Label>
                            <Input type="date" value={form.date} onChange={setInput("date")} required className="h-11 sm:h-10 text-base sm:text-sm" data-testid="income-form-date" />
                        </div>
                        <div>
                            <Label className="eyebrow">Source</Label>
                            <Input value={form.source} onChange={setInput("source")} required placeholder="Company / client / payer" className="h-11 sm:h-10 text-base sm:text-sm" data-testid="income-form-source" />
                        </div>
                        <div>
                            <Label className="eyebrow">Category</Label>
                            <Select value={form.category} onValueChange={set("category")}>
                                <SelectTrigger data-testid="income-form-category"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="eyebrow">Amount ({form.currency})</Label>
                            <Input type="number" inputMode="decimal" value={form.amount} onChange={setInput("amount")} required className="h-11 sm:h-10 text-lg sm:text-sm font-mono-figure" data-testid="income-form-amount" />
                        </div>
                        <div>
                            <Label className="eyebrow">TDS deducted</Label>
                            <Input type="number" value={form.tds} onChange={setInput("tds")} />
                        </div>
                        <div>
                            <Label className="eyebrow">Business / Personal</Label>
                            <Select value={form.business_personal} onValueChange={set("business_personal")}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Personal">Personal</SelectItem>
                                    <SelectItem value="Business">Business</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="eyebrow">Bank account</Label>
                            <Select value={form.bank_account_id || "none"} onValueChange={(v) => set("bank_account_id")(v === "none" ? "" : v)}>
                                <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">— none —</SelectItem>
                                    {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.bank_name} · ••{b.last_4}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center gap-3 border border-stone-200 rounded-md px-3 py-2.5">
                            <div className="flex-1">
                                <div className="text-sm text-stone-900">Recurring</div>
                                <div className="text-[11px] text-stone-500">Extrapolated into cash flow forecast</div>
                            </div>
                            <Switch checked={form.recurring} onCheckedChange={set("recurring")} data-testid="income-form-recurring" />
                        </div>
                        {form.recurring && (
                            <>
                                <div>
                                    <Label className="eyebrow">Frequency</Label>
                                    <Select value={form.frequency} onValueChange={set("frequency")}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {meta.frequencies.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="eyebrow">Next expected date</Label>
                                    <Input type="date" value={form.next_expected_date} onChange={setInput("next_expected_date")} />
                                </div>
                            </>
                        )}
                        <div className="flex items-center gap-3 border border-stone-200 rounded-md px-3 py-2.5 md:col-span-2">
                            <div className="flex-1">
                                <div className="text-sm text-stone-900">Already received?</div>
                                <div className="text-[11px] text-stone-500">Turn off if this is a pending receivable / invoice</div>
                            </div>
                            <Switch checked={form.received} onCheckedChange={set("received")} data-testid="income-form-received" />
                        </div>
                        {!form.received && (
                            <>
                                <div>
                                    <Label className="eyebrow">Expected on</Label>
                                    <Input type="date" value={form.expected_date} onChange={setInput("expected_date")} />
                                </div>
                                <div>
                                    <Label className="eyebrow">Follow-up date</Label>
                                    <Input type="date" value={form.follow_up_date} onChange={setInput("follow_up_date")} />
                                </div>
                                <div>
                                    <Label className="eyebrow">Invoice #</Label>
                                    <Input value={form.invoice_number} onChange={setInput("invoice_number")} />
                                </div>
                                <div>
                                    <Label className="eyebrow">Invoice date</Label>
                                    <Input type="date" value={form.invoice_date} onChange={setInput("invoice_date")} />
                                </div>
                            </>
                        )}
                        <div>
                            <Label className="eyebrow">Client</Label>
                            <Input value={form.client} onChange={setInput("client")} />
                        </div>
                        <div>
                            <Label className="eyebrow">Project</Label>
                            <Input value={form.project} onChange={setInput("project")} />
                        </div>
                        <div className="md:col-span-2">
                            <Label className="eyebrow">Remarks</Label>
                            <Textarea rows={2} value={form.remarks} onChange={setInput("remarks")} />
                        </div>
                        <DialogFooter className="md:col-span-2 flex-col sm:flex-row-reverse gap-2 sm:gap-0 mt-2">
                            <Button type="submit" disabled={busy} className="bg-stone-900 hover:bg-stone-800 text-white w-full sm:w-auto h-12 sm:h-10 text-base sm:text-sm" data-testid="income-form-save">
                                {busy ? "Saving…" : editing ? "Save changes" : "Log income"}
                            </Button>
                            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="w-full sm:w-auto h-11 sm:h-10">Cancel</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
